// src/brand/BrandContext.jsx
// Provides the active brand (BoardWatch vs CityWatch) to the whole app.
//
// Selection priority:
//   1. A district page can call setBrandFromDistrict(districtRow) to pin the
//      brand to the tenant being viewed (e.g. /districts/sfbos => CityWatch).
//   2. Otherwise, marketing routes under /city-watch render CityWatch chrome.
//   3. Otherwise, the default brand (BoardWatch).
//
// setBrandFromDistrict also pins showAccountMenu: accounts only do something
// for a tenant that actually uses dashboard_gate/premium_gate, so the navbar
// "Sign in" pill is hidden on districts where both are 'none' (avoids a dead
// affordance on free/legacy tenants like school district demos).
//
// Both overrides are cleared on navigation away from /districts/* so that
// leaving a district dashboard restores default marketing chrome.
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { BRANDS, DEFAULT_BRAND, brandSlugForDistrict } from "./brands";

const BrandContext = createContext({
  ...BRANDS[DEFAULT_BRAND],
  showAccountMenu: true,
  setBrandFromDistrict: () => {},
});

export const useBrand = () => useContext(BrandContext);

export function BrandProvider({ children }) {
  const location = useLocation();
  const [override, setOverride] = useState(null); // slug pinned by a district page
  const [accountsOverride, setAccountsOverride] = useState(null); // bool pinned by a district page

  // Reset the district overrides when navigating off a district page so brand/
  // account-menu bleed doesn't linger on marketing routes.
  useEffect(() => {
    if (!location.pathname.startsWith("/districts/")) {
      setOverride(null);
      setAccountsOverride(null);
    }
  }, [location.pathname]);

  const locationSlug = location.pathname.startsWith("/city-watch") ? "citywatch" : null;
  const slug = override || locationSlug || DEFAULT_BRAND;
  const brand = BRANDS[slug] || BRANDS[DEFAULT_BRAND];
  const showAccountMenu = accountsOverride === null ? true : accountsOverride;

  const value = useMemo(
    () => ({
      ...brand,
      showAccountMenu,
      setBrandFromDistrict: (d) => {
        const s = brandSlugForDistrict(d);
        setOverride(BRANDS[s] ? s : null);
        const accountsRelevant =
          (d.dashboard_gate && d.dashboard_gate !== 'none') ||
          (d.premium_gate && d.premium_gate !== 'none');
        setAccountsOverride(Boolean(accountsRelevant));
      },
    }),
    [brand, showAccountMenu]
  );

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}
